import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getDashboard, fmtMoney, fmtNum, fmtPct, API } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { StatusBadge } from "@/components/common/StatusBadge";
import axios from "axios";
import {
  Sun, ArrowsClockwise, Lightning, Target, TrendUp, ShoppingCart, CurrencyDollar,
  ListChecks, Sparkle, CheckCircle, ArrowRight,
} from "@phosphor-icons/react";

const OWNER = "Tom";

function MetricPill({ label, value }) {
  return (
    <div className="rounded-xl bg-white/[0.04] border border-white/8 px-4 py-3">
      <div className="label-caps">{label}</div>
      <div className="font-head text-xl font-semibold mt-1">{value}</div>
    </div>
  );
}

export default function ExecutiveHome() {
  const { activeId, activeClient } = useClient();

  const brief = useQuery({
    queryKey: ["briefing", activeId],
    queryFn: () => axios.post(`${API}/ai/briefing`, { client_id: activeId, owner_name: OWNER }).then((r) => r.data),
    enabled: !!activeId, staleTime: 10 * 60 * 1000,
  });
  const dash = useQuery({ queryKey: ["dashboard", activeId], queryFn: () => getDashboard(activeId), enabled: !!activeId });

  const d = dash.data;
  const b = brief.data;

  return (
    <PageContainer>
      {/* Briefing hero */}
      <div className="card-matte brand-glow p-6 md:p-8 mb-6 relative overflow-hidden fade-up">
        <div className="absolute -top-24 -right-20 h-64 w-64 rounded-full bg-[#6B3DFF]/10 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-[#6B3DFF]/15 flex items-center justify-center"><Sun size={24} className="brand-text" weight="fill" /></div>
              <div>
                <div className="label-caps">AI Marketing Director</div>
                <h1 className="font-head text-2xl md:text-3xl font-semibold mt-0.5">
                  {brief.isLoading ? `Good morning, ${OWNER}.` : (b?.greeting || `Good morning, ${OWNER}.`)}
                </h1>
              </div>
            </div>
            <Button data-testid="refresh-briefing-btn" variant="ghost" size="sm" onClick={() => brief.refetch()} className="text-[#9a9a9a] hover:text-white shrink-0">
              <ArrowsClockwise size={16} className={brief.isFetching ? "ai-thinking" : ""} />
            </Button>
          </div>

          {brief.isLoading || brief.isFetching ? (
            <div className="text-[#9a9a9a] mt-5 ai-thinking">Analyzing {activeClient?.name}'s performance…</div>
          ) : b ? (
            <>
              <p className="text-[#cfcfcf] mt-4 max-w-3xl leading-relaxed">{b.summary}</p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
                <MetricPill label="Yesterday Revenue" value={fmtMoney(b.yesterday?.revenue)} />
                <MetricPill label="Yesterday Orders" value={fmtNum(b.yesterday?.orders)} />
                <MetricPill label="Subscribers" value={`${fmtNum(b.metrics?.subscribers)} (+${b.metrics?.sub_growth}%)`} />
                <MetricPill label="Awaiting Approval" value={fmtNum(b.metrics?.pending_approvals)} />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                <div>
                  <div className="label-caps mb-3 flex items-center gap-2"><TrendUp size={14} className="brand-text" /> Trends Detected</div>
                  <ul className="space-y-2">
                    {b.trends?.map((t, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#cfcfcf]"><span className="brand-text mt-1.5 h-1 w-1 rounded-full bg-[#6B3DFF] shrink-0" />{t}</li>
                    ))}
                  </ul>
                  <div className="label-caps mb-3 mt-5 flex items-center gap-2"><ListChecks size={14} className="brand-text" /> Today's Priorities</div>
                  <ul className="space-y-2">
                    {b.priorities?.map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#cfcfcf]"><CheckCircle size={16} className="text-emerald-400 mt-0.5 shrink-0" />{p}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="label-caps mb-3 flex items-center gap-2"><Sparkle size={14} className="brand-text" weight="fill" /> AI Recommendations</div>
                  <div className="space-y-2.5">
                    {b.recommendations?.map((r, i) => (
                      <div key={i} data-testid={`brief-rec-${i}`} className="p-3 rounded-lg bg-white/[0.03] border border-white/8">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-semibold text-sm">{r.title}</div>
                          <span className="text-xs font-bold brand-text shrink-0">{r.impact}</span>
                        </div>
                        <div className="text-xs text-[#9a9a9a] mt-1">{r.reason}</div>
                        <div className="text-[10px] uppercase tracking-wider text-[#5a5a5a] mt-1.5">{r.category}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-[#9a9a9a] mt-5">Could not load briefing. Try refresh.</div>
          )}
        </div>
      </div>

      {/* KPI snapshot row */}
      {d && (
        <>
        <div className="flex items-center gap-2 mb-3">
          <span data-testid="data-source-badge" className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${d.data_source === "shopify" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/25" : "bg-white/5 text-[#9a9a9a] border-white/10"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${d.data_source === "shopify" ? "bg-emerald-400" : "bg-[#7a7a7a]"}`} />
            {d.data_source === "shopify" ? "Live Shopify data" : "No Shopify data connected"}
          </span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card-matte p-5"><div className="flex items-center gap-2 label-caps"><CurrencyDollar size={14} className="brand-text" /> Weekly Revenue</div><div className="font-head text-2xl font-semibold mt-2">{fmtMoney(d.revenue)}</div><div className="text-xs text-emerald-400 mt-1">+{d.revenue_delta}%</div></div>
          <div className="card-matte p-5"><div className="flex items-center gap-2 label-caps"><ShoppingCart size={14} /> Orders</div><div className="font-head text-2xl font-semibold mt-2">{fmtNum(d.orders)}</div><div className="text-xs text-emerald-400 mt-1">+{d.orders_delta}%</div></div>
          <div className="card-matte p-5"><div className="flex items-center gap-2 label-caps"><Target size={14} className="brand-text" /> Blended ROAS</div><div className="font-head text-2xl font-semibold mt-2">{d.roas}x</div><div className="text-xs text-[#7a7a7a] mt-1">{fmtMoney(d.total_ad_spend)} spend</div></div>
          <div className="card-matte p-5"><div className="flex items-center gap-2 label-caps"><Lightning size={14} className="brand-text" /> Email Revenue</div><div className="font-head text-2xl font-semibold mt-2">{fmtMoney(d.email_revenue)}</div></div>
        </div>
        </>
      )}

      {/* Goals */}
      <GoalsStrip />
    </PageContainer>
  );
}

function GoalsStrip() {
  const { activeId } = useClient();
  const { data: goals = [] } = useQuery({
    queryKey: ["data", "goals", activeId],
    queryFn: () => axios.get(`${API}/data/goals`, { params: { client_id: activeId } }).then((r) => r.data),
    enabled: !!activeId,
  });
  if (!goals.length) return null;
  return (
    <div className="card-matte p-5 mt-4">
      <div className="label-caps mb-4">Monthly Goals</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {goals.map((g) => {
          const pct = g.target ? Math.min(100, Math.round((g.current / g.target) * 100)) : 0;
          const fmt = (v) => g.unit === "$" ? fmtMoney(v) : `${v}${g.unit || ""}`;
          return (
            <div key={g.id} data-testid="goal-item">
              <div className="flex justify-between text-sm"><span className="text-[#cfcfcf]">{g.label}</span><span className="brand-text font-semibold">{pct}%</span></div>
              <Progress value={pct} className="h-2 bg-white/10 [&>div]:bg-[#6B3DFF] my-2" />
              <div className="text-xs text-[#7a7a7a]">{fmt(g.current)} / {fmt(g.target)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
